import { memo } from 'react';
import VideosSections from '../videos-section';
import { resolveRemainingExerciseTime } from '../../../utilis/helpers';
import { resolveExerciseReadoutFlags } from '../../../features/exercise/helpers';
import MobileScoreFrameRibbon from '../../mobile-score-frame-ribbon';
import PerformanceReadoutOverlay from '../performance-readout-overlay';
import tw from 'twin.macro';

const VideoAndCommentSide = ({
  inPerformance,
  videoPlayerRef,
  postPerformance,
  containerStyle,
  videoSrc,
  hasError,
  // Reference-video playback state for the patient (see use-connection-status).
  videoStatus,
  isFalseStage,
  counter,
  totalReps,
  isRightSide,
  exerciseType,
  time,
  totalTime,
  canStartUserTimer,
  isStackedMobile,
  currentScore,
  // False whenever the exercise is offline — NO_MOTION_CAPTURE, or no live media
  // source behind a MOTION_CAPTURE exercise. Nothing pose-derived is shown then;
  // only the countdown, the prescribed totals and this reference video.
  isOnline = true,
  // Camera off (session setting, backend per-movement flag, or no live source)
  // means nothing is scoring the movement — the page passes false and only the
  // counter/timer totals stay on screen.
  showQuality = true,
  // REP_BASED at 0 completed reps: the score has nothing behind it yet, so the
  // chips show "--" instead of a misleadingly literal "0%". Every other type's
  // score means something from the first frame, hence the true default.
  hasQualityScore = true,
  scoreStyles,
  currentFps,
  frontendFps,
  // Whether the score/FPS debug pill is actually showing right now — passed
  // straight through to PerformanceReadoutOverlay, which needs it to decide
  // whether the TIMED_REPS countdown chip has to duck under it.
  hasFpsOverlay = false,
  holdTelemetry,
  // Landscape phone (the redesign): this side is a rounded card inside a padded
  // grid, so the reference video sizes to the cell instead of the old full-bleed
  // 100vw/100vh box. The page passes showOverlayReadouts=false for landscape and
  // renders the readout overlay itself against the camera card instead — see
  // exercise/index.jsx — so fitsContainer only affects sizing/positioning here,
  // not which readouts show.
  fitsContainer = false,
  showOverlayReadouts = true,
}) => {
  const isFlipShadow = localStorage.getItem('isFlip');
  const parseSsFlipShadow = JSON.parse(isFlipShadow);
  // `type` (REP_BASED / TIMED_REPS / TIMED_HOLD) decides whether a rep counter
  // exists at all and whether a countdown is shown — REP_BASED ends on the reps,
  // so it gets no timer, online or offline. Offline TIMED_REPS counts no reps,
  // so its countdown becomes the only live readout.
  const { showsCounter, showsTimer, isTimedReps } = resolveExerciseReadoutFlags(exerciseType, {
    isOffline: !isOnline,
  });
  const isPerformanceActive = inPerformance && canStartUserTimer;
  // Portrait mobile moves these three readouts into MobileExerciseStatBar, the
  // band between the camera and this video (see the stacked branch in
  // exercise/index.jsx). Suppress the overlay copies there so they don't
  // double up on top of the camera feed.
  const showsOverlayReadouts = isPerformanceActive && !isStackedMobile && showOverlayReadouts;
  // Both layouts that own their own cell size the video the same way; only the
  // full-bleed desktop grid still wants the 100vw/absolute box.
  const fillsCell = isStackedMobile || fitsContainer;
  const shouldShowCounter = showsOverlayReadouts && showsCounter;
  const fpsValue = currentFps ?? frontendFps ?? null;
  const shouldShowTimer = showsOverlayReadouts && showsTimer;
  // Hold time is no longer a pill here — HoldTimerRing renders it as a progress
  // ring over the camera feed (see the layout branches in exercise/index.jsx).
  const timerValue = shouldShowTimer
    ? resolveRemainingExerciseTime({ time, totalTimeSeconds: totalTime })
    : null;

  return (
    <div
      css={[
        containerStyle,
        postPerformance ? tw`hidden` : tw`block`,
        // On mobile portrait the reference video stacks below the webcam
        // (see isStackedMobileLayout in exercise/index.jsx) instead of the
        // desktop/tablet/landscape side-by-side grid cell.
        // Stacked mobile sizes off its flex cell (the page owns the dvh height
        // there). Desktop keeps the original h-screen it always had.
        fillsCell ? tw`relative w-full h-full` : tw`relative h-full h-screen`,
      ]}
    >
      <VideosSections
        hasError={hasError}
        inPerformance={inPerformance}
        isRightSide={
          parseSsFlipShadow
            ? typeof isRightSide === 'boolean'
              ? !isRightSide
              : parseSsFlipShadow
            : typeof isRightSide === 'boolean'
              ? isRightSide
              : parseSsFlipShadow
        }
        videoPlayerRef={videoPlayerRef}
        videoSrc={videoSrc}
        compact={false}
        isStackedMobile={fillsCell}
        videoStatus={videoStatus}
      />
      {/* {isStackedMobile && isPerformanceActive && isOnline && (
        <MobileScoreFrameRibbon
          score={currentScore}
          hasScore={hasQualityScore}
          fps={fpsValue}
          isFalseStage={isFalseStage}
          scoreStyles={scoreStyles}
        />
      )} */}

      {/* Desktop/stacked-mobile only: landscape phone renders this same overlay
          itself, anchored to the camera card instead (exercise/index.jsx),
          since `fixed` here would escape to the viewport's actual top-left
          corner — the camera cell, not this one — rather than this card. */}
      <PerformanceReadoutOverlay
        anchor={fitsContainer ? 'absolute' : 'fixed'}
        inPerformance={inPerformance}
        isFalseStage={isFalseStage}
        counter={counter}
        totalReps={totalReps}
        shouldShowCounter={shouldShowCounter}
        shouldShowTimer={shouldShowTimer}
        isTimedReps={isTimedReps}
        timerValue={timerValue}
        exerciseType={exerciseType}
        showQuality={showsOverlayReadouts && showQuality}
        currentScore={currentScore}
        hasQualityScore={hasQualityScore}
        hasFpsOverlay={hasFpsOverlay}
      />
    </div>
  );
};

export default memo(VideoAndCommentSide);
